import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList, FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import moment from "moment";

const ManageAttendanceList = () => {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const staff_id = clientData?.master_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { translations } = useSelector((state) => state.language);

  // Fetch attendance list
  useEffect(() => {
    if (staff_id) {
      fetchAttendanceList();
    }
  }, [staff_id, pagination.pageIndex, pagination.pageSize, apiSearch]);

  // Fetch attendance list from API
  const fetchAttendanceList = async () => {
    setLoading(true);
    try {
      const payload = {
        staff_id: parseInt(staff_id),
        page: String(pagination.pageIndex + 1),
        limit: String(pagination.pageSize),
      };

      const response = await AxiosInstance.post(
        `/attendance/listAttendance`,
        payload,
      );

      if (response?.data?.status) {
        const attendanceData = response.data.result || [];
        const transformedData = attendanceData.map((item, index) => ({
          id: `${item.class_id}-${item.section_id}-${item.attendance_date}`,
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          class_id: item.class_id,
          class: item.class,
          section_id: item.section_id,
          section: item.section_id,
          attendance_date: item.attendance_date
            ? moment(item.attendance_date).format("DD/MM/YYYY")
            : "",
          total_students: item.total_students,
        }));
        setData(transformedData);
        setRowCount(response.data.totalCount || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching attendance list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.class?.toLowerCase().includes(searchLower) ||
        String(item.section)?.toLowerCase().includes(searchLower) ||
        item.attendance_date?.toLowerCase().includes(searchLower),
    );
  }, [data, apiSearch]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleAdd = () => {
    navigate("/user/attendanceCreate");
  };

  const handleSelect = (row) => {
    navigate("/user/attendanceCreate", {
      state: {
        class_id: row.class_id,
        section_id: row.section_id,
        attendance_date: row.attendance_date,
        data: row,
      },
    });
  };

  // Check if current date exists in data list
  const today = moment().format("DD/MM/YYYY");
  const isTodayInList = data.some((item) => item.attendance_date === today);

  // Table columns matching the image: Class, Section, Date, Select
  const columns = useMemo(
    () => [
      {
        accessorKey: "class",
        header: translations.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "section",
        header: translations.labels?.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "attendance_date",
        header: translations.labels?.date || "Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const isToday = row?.original?.attendance_date === today;

          return isToday ? (
            <button
              className={styles.viewBtn}
              onClick={() => handleSelect(row.original)}
              title={translations.labels?.view || "View"}
            >
              <FaEye />
            </button>
          ) : (
            "-"
          );
        },
      },
    ],
    [translations],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.attendanceList || "Attendance List"}
        </h1>
      </div>

      {/* Filters and Action Buttons */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          <div className="col-lg-12 px-2 col-md-12 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
                // disabled={isTodayInList}
              >
                {translations.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        placeholder={translations.labels.searchButton}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={false}
        enableFiltering={false}
      />
    </div>
  );
};

export default ManageAttendanceList;
